import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

function readViewportSize() {
  if (typeof window === 'undefined') return { width: 0, height: 0 };

  // On real mobile devices (notably iOS Safari), `innerWidth` can temporarily
  // report a larger "layout viewport" width until the browser UI settles.
  // `visualViewport` and `documentElement.clientWidth` track CSS breakpoints
  // more reliably, and also update on scroll/UI chrome changes.
  const vv = window.visualViewport;
  const width = Math.round(
    (vv?.width ?? document.documentElement?.clientWidth ?? window.innerWidth) ||
      0,
  );
  const height = Math.round(
    (vv?.height ??
      document.documentElement?.clientHeight ??
      window.innerHeight) || 0,
  );

  return { width, height };
}

export function useWindowSize() {
  const [width, setWidth] = useState<number>(() =>
    readViewportSize().width,
  );
  const [height, setHeight] = useState<number>(() =>
    readViewportSize().height,
  );
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const rafIdRef = useRef<number>(0);

  const updateSizeNow = () => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      setIsResizing(false);
      const next = readViewportSize();
      setWidth(next.width);
      setHeight(next.height);
    });
  };

  const updateSizeDebounced = useMemo(() => debounce(updateSizeNow, 50), []);

  useEffect(() => {
    // Ensure the initial client render has correct dimensions.
    if (typeof window === 'undefined') return;
    updateSizeNow();

    const handleResize = () => {
      setIsResizing(true);
      updateSizeDebounced();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    // `pageshow` covers bfcache restores where sizes can be stale.
    window.addEventListener('pageshow', handleResize);

    // iOS Safari often "fixes" viewport size after scroll/UI chrome changes.
    // visualViewport emits resize/scroll for those.
    const vv = window.visualViewport;
    vv?.addEventListener('resize', handleResize);
    vv?.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('pageshow', handleResize);
      vv?.removeEventListener('resize', handleResize);
      vv?.removeEventListener('scroll', handleResize);
      updateSizeDebounced.cancel();
      cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  return { width, height, isResizing };
}
